import { Button, Grid, Input, Slider, Typography } from '@mui/material';
import React, { useContext, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { LoggingContext } from '../Context/LoggingContext/LoggingContext';
import { UserContext } from '../Context/UserContext/UserContextProvider';
import { MeasureProps } from '../tools/generalProperties';
import { TeeMeasureLabel } from '../types/databaseTables/idealteemeasures';

import CustomIcon from './CustomIcon';

function convertInchesToCm(inches: number): number {
  return Math.round(inches * 2.57);
}

function convertCmToInches(cm: number): number {
  return Math.round((cm / 2.57) * 2) / 2;
}

interface IdealMeasureSliderProps {
  eachMeasureProperties: MeasureProps;
  measureLabel: TeeMeasureLabel;
  updateParentState: (labelToUpdate: TeeMeasureLabel, newMeasure: number) => void;
  parentValue: number;
  setHelpSectionOpen?: () => void;
}

function IdealMeasureSlider({
  eachMeasureProperties,
  measureLabel,
  updateParentState,
  parentValue,
  setHelpSectionOpen,
}: IdealMeasureSliderProps) {
  const location = useLocation();
  const { userDetailsState } = useContext(UserContext);
  const { addLog } = useContext(LoggingContext);

  function convertForParent(valueToConvert: number): number {
    if (userDetailsState.unitcm === 1) {
      return valueToConvert;
    }
    return convertInchesToCm(valueToConvert) || 0;
  }

  function convertFromParent(valueToConvert: number): number {
    if (userDetailsState.unitcm === 1) {
      return valueToConvert;
    }
    return convertCmToInches(valueToConvert) || 0;
  }

  const thisUnits = userDetailsState.unitcm || 1;
  // We have to convert slider min/max/step and value to cm/inches. So localState can be in inches or cm. Then we'll convert back to cn when we update parent state.
  const [localValue, setLocalValue] = React.useState(convertFromParent(parentValue));
  const roundingNumber = thisUnits === 1 ? 2 : 4;
  const roundedValue = Math.round((localValue + Number.EPSILON) * roundingNumber) / roundingNumber; // round to nearest 0.5 with "2"
  const step = thisUnits === 1 ? 0.5 : 0.25;

  const handleSliderChange = (event: Event, newValue: number | number[]) => {
    setLocalValue(newValue as number);
    const delayDebounceFn = setTimeout(() => {
      addLog(`adjust with slider input in IdealMeasureSlider`, location.pathname);
    }, 150);
    return () => clearTimeout(delayDebounceFn);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    addLog(`adjust with text input in IdealMeasureSlider`, location.pathname);

    const newValue =
      event.target.value === ''
        ? 0
        : Math.round(Number(event.target.value) * roundingNumber) / roundingNumber; // round to nearest 0.01
    setLocalValue(newValue);
  };

  function handleAdjustWithButton(plusOrMinus: '+' | '-') {
    addLog(`adjust with ${plusOrMinus} button on IdealMeasureSlider`, location.pathname);
    if (plusOrMinus === '+') {
      setLocalValue((prev) => prev + step);
    }
    if (plusOrMinus === '-') {
      setLocalValue((prev) => prev - step);
    }
  }

  const handleBlur = () => {
    if (parentValue < 0) {
      setLocalValue(0);
    } else if (parentValue > 100) {
      setLocalValue(Math.round(eachMeasureProperties.max / thisUnits));
    }
  };

  // Debounce update to parentState from local state
  useEffect(() => {
    function updateWhenLocalValueChanges() {
      // Return out if local state matches parentState
      if (localValue === convertFromParent(parentValue)) return;
      // Else update parent state
      updateParentState(measureLabel, convertForParent(localValue));
    }
    const delayDebounceFn = setTimeout(() => {
      updateWhenLocalValueChanges();
    }, 150);
    return () => clearTimeout(delayDebounceFn);
  }, [localValue]);

  // Update local value when userDetailsState (context) is updated
  useEffect(() => {
    if (
      localValue === convertFromParent(userDetailsState.idealGarmentMeasurements[measureLabel] || 0)
    )
      return;
    setLocalValue(convertFromParent(userDetailsState.idealGarmentMeasurements[measureLabel] || 0));
  }, [userDetailsState.idealGarmentMeasurements[measureLabel]]);

  return (
    <Grid item xs={12} lg={5} md={5}>
      <Grid
        container
        justifyContent='space-between'
        sx={{
          display: 'grid',
          gridTemplateColumns: '130px 1fr 130px',
          margin: { xs: '0 8px', sm: 0 },
        }}
      >
        {/* Measure Label */}
        <Typography sx={{ whiteSpace: 'nowrap' }} id='input-slider'>
          {eachMeasureProperties.name}
        </Typography>
        {/* Middle button/message */}
        <Grid
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            position: 'relative',
            '& p': {
              position: 'absolute',
              top: 40,
            },
          }}
        />
        {/* Plus/Minus Buttons */}
        <Grid sx={{ padding: 0 }}>
          {['-', '+'].map((x) => (
            <Button
              sx={{
                boxShadow: '1px 1px 2px 1px rgba(0, 0, 255, 30%)',
                opacity: '50%',
                padding: 0,
              }}
              key={x}
              onClick={() => handleAdjustWithButton(x as '+' | '-')}
            >
              {x}
            </Button>
          ))}
        </Grid>
      </Grid>
      {/* Measurement Image, Slider, Input */}
      <Grid container spacing={2} alignItems='center'>
        <Grid item sx={{ cursor: 'pointer' }} onClick={setHelpSectionOpen}>
          <CustomIcon imageRef={eachMeasureProperties.imageRef} />
        </Grid>
        <Grid item xs>
          <Slider
            value={roundedValue}
            onChange={handleSliderChange}
            aria-labelledby='input-slider'
            min={Math.round(eachMeasureProperties.min / thisUnits)}
            max={Math.round(eachMeasureProperties.max / thisUnits)}
            step={step}
            sx={{
              '& .MuiSlider-thumb': {
                height: 20,
                width: 20,
                backgroundColor: '#fff',
                border: '2px solid currentColor',
              },
            }}
          />
        </Grid>
        <Grid item>
          <Input
            sx={{ width: 50 }}
            value={roundedValue}
            margin='dense'
            onChange={handleInputChange}
            onBlur={handleBlur}
            inputProps={{
              step,
              type: 'number',
              min: 0,
              'aria-labelledby': 'input-slider',
            }}
          />
          <Typography id='inchesorcm' sx={{ color: 'grey' }}>
            {thisUnits === 1 ? 'cm' : 'inches'}
          </Typography>
        </Grid>
      </Grid>
    </Grid>
  );
}
IdealMeasureSlider.defaultProps = {
  setHelpSectionOpen: () => null,
};
export default IdealMeasureSlider;
